import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LoginIcon from '@mui/icons-material/Login';
import PeopleIcon from '@mui/icons-material/People';
import ShareIcon from '@mui/icons-material/Share';
import GroupsIcon from '@mui/icons-material/Groups';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import apiService from '../services/ApiService';
import authService from '../services/AuthService';
import adMobService from '../services/AdMobService';
import configService from '../services/ConfigService';
import gameStateManager, { MAX_WORDS } from '../services/GameStateManager';
import soundVibrationService from '../services/SoundVibrationService';
import groupService from '../services/GroupService';
import LoadingSpinner from '../components/LoadingSpinner';
import TopicDisplay from '../components/TopicDisplay';
import WordSubmissionForm from '../components/WordSubmissionForm';
import ShareModal from '../components/ShareModal';
import SuggestTopicSection from '../components/SuggestTopicSection';
import WelcomeModal from '../components/WelcomeModal';
import tutorialService from '../services/TutorialService';
import { useLoginModal } from '../context/LoginModalContext';
import { debugLog, debugError } from '../utils/debugUtils';
import useCountUp from '../hooks/useCountUp';
import AdSenseBanner from '../components/AdSenseBanner';

const NEWS_BANNER_STORAGE_KEY = 'concordare_groups_news_banner_dismissed';

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openLoginModal = useLoginModal();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submittedWords, setSubmittedWords] = useState([]);
  const [bannedWords, setBannedWords] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [playersCount, setPlayersCount] = useState(null);
  const [welcomeOpen, setWelcomeOpen] = useState(() => tutorialService.shouldShowTutorial());
  const [isPastScheduler, setIsPastScheduler] = useState(false);
  const [myGroups, setMyGroups] = useState(null);
  const [newsBannerDismissed, setNewsBannerDismissed] = useState(
    () => localStorage.getItem(NEWS_BANNER_STORAGE_KEY) === '1'
  );
  const animatedPlayersCount = useCountUp(playersCount, { msPerUnit: 8, minDuration: 800, maxDuration: 2500 });

  const user = authService.getUser();
  const isAuthenticated = authService.isAuthenticated();

  const loadTopic = useCallback(async () => {
    try {
      const data = await apiService.get('/api/v1/topics/today');
      setTopic(data);
      debugLog('[HomePage] Topic loaded:', data);

      if (user && data) {
        try {
          const serverWords = await apiService.get(`/api/v1/game/user-words/${data.id}`);
          const words = serverWords || [];
          setSubmittedWords(words);
          gameStateManager.syncWords(user.id, data.id, words.map((w) => w.raw)).catch(() => {});
          debugLog('[HomePage] Words loaded from server:', words);
        } catch {
          const words = await gameStateManager.getWords(user.id, data.id);
          setSubmittedWords(words.map((raw) => ({ raw, canonical: null })));
          debugLog('[HomePage] Words loaded from IndexedDB (fallback):', words);
        }
      }

      // Cargar contador de jugadores para la tarjeta de compartir
      apiService.get('/api/v1/game/players-today')
        .then(data => setPlayersCount(data?.count ?? null))
        .catch(() => {});
    } catch (err) {
      debugError('[HomePage] Error loading topic:', err);
      setTopic(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadTopic();
    configService.load().then(() => setIsPastScheduler(configService.isPastSchedulerTime()));

    apiService.get('/api/v1/banned-words/list', false)
      .then((list) => setBannedWords(list || []))
      .catch(() => setBannedWords([]));

    const handleUnauthorized = () => openLoginModal?.();
    window.addEventListener('concordare:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('concordare:unauthorized', handleUnauthorized);
  }, [loadTopic, openLoginModal]);

  useEffect(() => {
    if (!isAuthenticated) return;
    groupService
      .listMine()
      .then((data) => setMyGroups(Array.isArray(data) ? data : []))
      .catch(() => setMyGroups([]));
  }, [isAuthenticated]);

  const handleWordSubmitted = async (word) => {
    if (!topic || !user) return;

    try {
      await apiService.post('/api/v1/game/play-word', {
        userId: user.id,
        topicId: topic.id,
        word,
      });

      setSubmittedWords((prev) => [...prev, { raw: word.trim(), canonical: null }]);
      gameStateManager.saveWord(user.id, topic.id, word).catch(() => {});

      await soundVibrationService.vibrateSuccess();
      setSnackbar({ open: true, message: t('home.wordSentSuccess'), severity: 'success' });
      debugLog('[HomePage] Word submitted:', word);
    } catch (err) {
      debugError('[HomePage] Error submitting word:', err);
      await soundVibrationService.vibrateError();
      const message = err.code != null
        ? t(`apiErrors.${err.code}`, t('home.wordSentError'))
        : t('home.wordSentError');
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  if (loading) return <LoadingSpinner />;

  const allWordsSent = submittedWords.length >= MAX_WORDS;

  return (
    <Box className="page-content" sx={{ gap: 2, maxWidth: 600, mx: 'auto', width: '100%' }}>
      {!newsBannerDismissed && (
        <Card
          sx={{
            width: '100%',
            position: 'relative',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #1976d2, #764ba2)',
            color: 'white',
            '&:hover': { boxShadow: 6 },
          }}
          onClick={() => (isAuthenticated ? navigate('/groups') : openLoginModal(true))}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              localStorage.setItem(NEWS_BANNER_STORAGE_KEY, '1');
              setNewsBannerDismissed(true);
            }}
            aria-label={t('common.close')}
            sx={{ position: 'absolute', top: 4, right: 4, color: 'inherit' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2, pr: 4 }}>
            <GroupsIcon sx={{ fontSize: 36 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Chip
                size="small"
                label={t('home.newsBadge')}
                sx={{ bgcolor: 'white', color: '#764ba2', fontWeight: 700, mb: 0.5 }}
              />
              <Typography variant="subtitle1" fontWeight={700}>
                {t('home.newsBannerTitle')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {t('home.newsBannerMessage')}
              </Typography>
            </Box>
            <ChevronRightIcon />
          </CardContent>
        </Card>
      )}

      {!topic ? (
        <Card sx={{ width: '100%', mt: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              {t('home.noTopicTitle')}
            </Typography>
            <Typography color="text.secondary">{t('home.noTopicMessage')}</Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <TopicDisplay topic={topic} />

          {playersCount !== null && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'white' }}>
              <PeopleIcon fontSize="small" sx={{ opacity: 0.85 }} />
              <Typography variant="body2" sx={{ opacity: 0.85, textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                {t('results.playersToday', { count: animatedPlayersCount })}
              </Typography>
            </Box>
          )}

          {!isAuthenticated ? (
            <Card sx={{ width: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t('home.loginToPlay')}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  {t('home.loginToPlayMessage')}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LoginIcon />}
                  onClick={() => openLoginModal(true)}
                >
                  {t('auth.login')}
                </Button>
              </CardContent>
            </Card>
          ) : allWordsSent ? (
            <Card sx={{ width: '100%', bgcolor: 'success.light' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <EmojiEventsIcon sx={{ fontSize: 48, color: 'success.dark', mb: 1 }} />
                <Typography variant="h6" fontWeight={700} color="success.dark" gutterBottom>
                  {t('home.allWordsSent')}
                </Typography>
                <Typography variant="body2" color="success.dark" mb={2}>
                  {t('home.allWordsSentMessage')}
                </Typography>
                <Button variant="contained" color="success" onClick={() => navigate(adMobService.getResultsPath())}>
                  {t('home.viewResults')}
                </Button>
              </CardContent>
            </Card>
          ) : isPastScheduler ? (
            <Card sx={{ width: '100%', bgcolor: 'info.light' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <EmojiEventsIcon sx={{ fontSize: 48, color: 'info.dark', mb: 1 }} />
                <Typography variant="h6" fontWeight={700} color="info.dark" gutterBottom>
                  {t('home.resultsReadyTitle')}
                </Typography>
                <Typography variant="body2" color="info.dark" mb={2}>
                  {t('home.resultsReadyNoParticipationMessage')}
                </Typography>
                <Button variant="contained" color="info" onClick={() => navigate(adMobService.getResultsPath())}>
                  {t('home.viewResults')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <WordSubmissionForm
              submittedWords={submittedWords}
              maxWords={MAX_WORDS}
              onSubmit={handleWordSubmitted}
              disabled={allWordsSent}
              bannedWords={bannedWords}
            />
          )}

          {isAuthenticated && submittedWords.length > 0 && (
            <>
              <Card sx={{ width: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {t('home.yourWords')} ({submittedWords.length}/{MAX_WORDS})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {submittedWords.map((word, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          borderRadius: 4,
                          px: 2,
                          py: 0.5,
                          fontSize: '0.9rem',
                          fontWeight: 500,
                        }}
                      >
                        {word.raw}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<ShareIcon />}
                  onClick={() => setShareModalOpen(true)}
                  sx={{
                    background: 'linear-gradient(90deg, #1976d2, #764ba2)',
                    color: 'white',
                    fontWeight: 700,
                    px: 3,
                    borderRadius: 3,
                    '&:hover': { background: 'linear-gradient(90deg, #1565c0, #6a1b9a)' },
                  }}
                >
                  {t('share.buttonPending')}
                </Button>
              </Box>
            </>
          )}
        </>
      )}

      {isAuthenticated && myGroups !== null && (
        <Card
          sx={{ width: '100%', cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
          onClick={() => navigate(myGroups.length === 1 ? `/groups/${myGroups[0].id}` : '/groups')}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <GroupsIcon color="primary" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {myGroups.length === 1
                  ? t('home.groupsCardTitleSingle', { name: myGroups[0].name })
                  : myGroups.length > 1
                    ? t('home.groupsCardTitleMultiple')
                    : t('home.groupsCardTitleEmpty')}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {myGroups.length === 1
                  ? t('home.groupsCardSubtitleSingle')
                  : myGroups.length > 1
                    ? t('home.groupsCardSubtitleMultiple')
                    : t('home.groupsCardSubtitleEmpty')}
              </Typography>
            </Box>
            <ChevronRightIcon color="action" />
          </CardContent>
        </Card>
      )}

      {isAuthenticated && <SuggestTopicSection />}

      <AdSenseBanner />

      <WelcomeModal open={welcomeOpen} onClose={() => setWelcomeOpen(false)} />

      <ShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        mode="pending"
        topic={topic?.topic}
        topicDate={topic?.date}
        userWords={submittedWords}
        playersToday={playersCount}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
